import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import store from '../../store';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  NotificationItem as NotificationRecord,
} from '../../api/NotificationApi';

const PAGE_SIZE = 20;
const SYNC_EVENT = 'notification-invalidation';

type CenterContextValue = {
  websiteGuid: string;
  items: NotificationRecord[];
  unreadCount: number | null;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
  moreError: boolean;
  nextCursor: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (item: NotificationRecord) => Promise<void>;
  markAllRead: () => Promise<void>;
  pending: string | null;
  mutationError: boolean;
};

const CenterContext = createContext<CenterContextValue | null>(null);

function channelName(websiteGuid: string) {
  return `cbs-notifications:${process.env.NODE_ENV || 'unknown'}:${String(store.getState().user?.userId || 'unknown').toLowerCase()}:${websiteGuid}`;
}

function useCenter() {
  const value = useContext(CenterContext);
  if (!value) throw new Error('Notification components must be rendered inside NotificationCenter');
  return value;
}

function safeHref(item: NotificationRecord): string | null {
  if (!item.dataJson) return null;
  try {
    const data = JSON.parse(item.dataJson);
    return typeof data?.bookingId === 'string' && data.bookingId
      ? `#/bookings/booking/${encodeURIComponent(data.bookingId)}`
      : null;
  } catch { return null; }
}

function dateLabel(value: string) {
  return new Date(value).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function NotificationCenter({ websiteGuid, unreadCount, onUnreadCountChange, active = false, children }: { websiteGuid: string; unreadCount: number | null; onUnreadCountChange?: (count: number) => void; active?: boolean; children: React.ReactNode }) {
  const [items, setItems] = useState<NotificationRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState(false);
  const requestRef = useRef(0);
  const wasActiveRef = useRef(false);

  const merge = useCallback((incoming: NotificationRecord[], append: boolean) => {
    setItems(previous => {
      const all = append ? [...previous, ...incoming] : incoming;
      return all
        .filter((item, index, source) => source.findIndex(candidate => candidate.id === item.id) === index)
        .sort((a, b) => Date.parse(b.createdAtUtc) - Date.parse(a.createdAtUtc));
    });
  }, []);

  const load = useCallback(async (cursor: string | null = null) => {
    if (!websiteGuid) return;
    const requestId = ++requestRef.current;
    const append = Boolean(cursor);
    append ? setLoadingMore(true) : setLoading(true);
    append ? setMoreError(false) : setError(false);
    try {
      const page = await fetchNotifications({ websiteGuid, cursor, limit: PAGE_SIZE });
      if (requestId !== requestRef.current) return;
      merge(page.items, append);
      setNextCursor(page.nextCursor);
    } catch {
      if (requestId === requestRef.current) append ? setMoreError(true) : setError(true);
    } finally {
      if (requestId === requestRef.current) append ? setLoadingMore(false) : setLoading(false);
    }
  }, [merge, websiteGuid]);

  const refresh = useCallback(() => load(), [load]);
  const loadMore = useCallback(() => nextCursor ? load(nextCursor) : Promise.resolve(), [load, nextCursor]);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    wasActiveRef.current = false;
  }, [websiteGuid]);
  useEffect(() => {
    const opened = active && !wasActiveRef.current;
    wasActiveRef.current = active;
    if (opened) load();
  }, [active, load]);
  useEffect(() => {
    if (!websiteGuid || typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(channelName(websiteGuid));
    const refreshOnMessage = (event: MessageEvent) => { if (event.data?.type === SYNC_EVENT) refresh(); };
    channel.addEventListener('message', refreshOnMessage);
    return () => { channel.removeEventListener('message', refreshOnMessage); channel.close(); };
  }, [refresh, websiteGuid]);

  const broadcast = () => {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(channelName(websiteGuid));
      channel.postMessage({ type: SYNC_EVENT });
      channel.close();
    }
  };

  const markRead = async (item: NotificationRecord) => {
    if (item.readAtUtc || pending) return;
    const readAtUtc = new Date().toISOString();
    setPending(item.id); setMutationError(false);
    setItems(previous => previous.map(current => current.id === item.id ? { ...current, readAtUtc } : current));
    if (unreadCount !== null) onUnreadCountChange?.(Math.max(0, unreadCount - 1));
    try {
      await markNotificationRead(item.id);
    } catch {
      setItems(previous => previous.map(current => current.id === item.id ? { ...current, readAtUtc: null } : current));
      if (unreadCount !== null) onUnreadCountChange?.(unreadCount);
      setMutationError(true);
    } finally { setPending(null); }
  };

  const markAllRead = async () => {
    if (!websiteGuid || pending || !items.some(item => !item.readAtUtc)) return;
    setPending('all'); setMutationError(false);
    try {
      await markAllNotificationsRead(websiteGuid);
      const now = new Date().toISOString();
      setItems(previous => previous.map(item => ({ ...item, readAtUtc: item.readAtUtc || now })));
      onUnreadCountChange?.(0); broadcast();
    } catch { setMutationError(true); } finally { setPending(null); }
  };

  const value = useMemo(() => ({ websiteGuid, items, unreadCount, loading, loadingMore, error, moreError, nextCursor, refresh, loadMore, markRead, markAllRead, pending, mutationError }), [websiteGuid, items, unreadCount, loading, loadingMore, error, moreError, nextCursor, refresh, loadMore, markRead, markAllRead, pending, mutationError]);
  return <CenterContext.Provider value={value}>{children}</CenterContext.Provider>;
}

export function NotificationItem({ item }: { item: NotificationRecord }) {
  const { t } = useTranslation();
  const { markRead, pending } = useCenter();
  const href = safeHref(item);
  return <div className={`cbs-notification-item${item.readAtUtc ? '' : ' cbs-notification-item--unread'}`} onClick={() => markRead(item)} role="button" tabIndex={0}>
    <div className="d-flex align-items-start">
      <span className="cbs-notification-item__icon" aria-hidden="true"><i className="ion ion-md-notifications-outline" /></span>
      <div className="flex-grow-1">
        <div className="d-flex justify-content-between"><strong>{item.title || item.type}</strong><small className="text-muted ml-2">{dateLabel(item.createdAtUtc)}</small></div>
        {item.message && <div className="text-muted mt-1">{item.message}</div>}
        {!item.readAtUtc && <Button variant="link" size="sm" className="p-0 mt-2" disabled={pending !== null} onClick={event => { event.stopPropagation(); markRead(item); }}>{t('common:notifications.markRead', 'Mark as read')}</Button>}
        {href && <a className="d-block mt-1" href={href} onClick={() => markRead(item)}>{t('common:notifications.openBooking', 'Open booking')}</a>}
      </div>
    </div>
  </div>;
}

function NotificationStates({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { items, loading, error, refresh } = useCenter();
  if (loading) return <div className="text-center p-4"><Spinner animation="border" size="sm" aria-label={t('common:notifications.loading', 'Loading')} /></div>;
  if (error) return <Alert variant="danger" className="m-3">{t('common:notifications.loadError', 'Notifications could not be loaded.')} <Button variant="link" onClick={refresh}>{t('common:notifications.retry', 'Retry')}</Button></Alert>;
  if (!items.length) return <div className="text-center text-muted p-4">{t('common:notifications.empty', 'You have no notifications.')}</div>;
  return <>{items.slice(0, compact ? PAGE_SIZE : undefined).map(item => <NotificationItem key={item.id} item={item} />)}</>;
}

export function NotificationDropdown({ onViewAll, onClose }: { onViewAll: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const { items, markAllRead, pending, mutationError } = useCenter();
  return <div className="cbs-notification-dropdown" role="dialog" aria-label={t('common:notifications.title', 'Notifications')}>
    <div className="cbs-notification-dropdown__header"><strong>{t('common:notifications.title', 'Notifications')}</strong><Button variant="link" size="sm" disabled={pending !== null || !items.some(item => !item.readAtUtc)} onClick={markAllRead}>{t('common:notifications.markAllRead', 'Mark all as read')}</Button></div>
    {mutationError && <Alert variant="danger" className="m-2">{t('common:notifications.readError', 'The read status could not be saved. Please retry.')}</Alert>}
    <NotificationStates compact />
    <div className="cbs-notification-dropdown__footer"><Button variant="link" onClick={() => { onClose(); onViewAll(); }}>{t('common:notifications.viewAll', 'View all')}</Button></div>
  </div>;
}

export function NotificationModal({ show, onHide }: { show: boolean; onHide: () => void }) {
  const { t } = useTranslation();
  const { moreError, loadingMore, nextCursor, loadMore } = useCenter();
  return <Modal show={show} onHide={onHide} size="lg" centered scrollable>
    <Modal.Header closeButton><Modal.Title>{t('common:notifications.title', 'Notifications')}</Modal.Title></Modal.Header>
    <Modal.Body className="p-0"><NotificationStates />{moreError && <Alert variant="danger" className="m-3">{t('common:notifications.loadMoreError', 'More notifications could not be loaded.')}</Alert>}</Modal.Body>
    <Modal.Footer className="justify-content-center">{nextCursor && <Button variant="outline-primary" disabled={loadingMore} onClick={loadMore}>{loadingMore ? <Spinner animation="border" size="sm" /> : t('common:notifications.loadMore', 'View more')}</Button>}</Modal.Footer>
  </Modal>;
}

export function NotificationBell({ websiteGuid, unreadCount, onUnreadCountChange }: { websiteGuid: string; unreadCount: number | null; onUnreadCountChange?: (count: number) => void }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { const close = (event: MouseEvent) => { if (open && ref.current && !ref.current.contains(event.target as Node)) setOpen(false); }; document.addEventListener('mousedown', close); return () => document.removeEventListener('mousedown', close); }, [open]);
  return <NotificationCenter websiteGuid={websiteGuid} unreadCount={unreadCount} onUnreadCountChange={onUnreadCountChange} active={open}>
    <div ref={ref} className="cbs-notification-center">
      <button type="button" className="nav-link px-2 cbs-notification-nav position-relative btn btn-link" aria-label="Notifications" onClick={() => setOpen(value => !value)}>
        <i className="ion ion-md-notifications-outline cbs-notification-nav__icon" style={{ fontSize: '1.35rem', lineHeight: 1 }} />
        {unreadCount !== null && unreadCount > 0 && <span className="cbs-notification-nav__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      {open && <NotificationDropdown onClose={() => setOpen(false)} onViewAll={() => setModal(true)} />}
      <NotificationModal show={modal} onHide={() => setModal(false)} />
    </div>
  </NotificationCenter>;
}
